import { DestroyRef, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import {
  OutlookSyncConfigurationPayload,
  OutlookSyncHistoryEntry,
  OutlookSyncStatus,
  OutlookSyncType,
  SyncHistory,
  UserSyncConfiguration,
  UserSyncConfigurationService,
} from '@maximizer/outlook-sync/data-access';

export const HISTORY_PAGE_SIZE = 5;

const STATUS_MAP: Record<OutlookSyncStatus, OutlookSyncHistoryEntry['syncStatus']> = {
  [OutlookSyncStatus.Complete]: 'Complete',
  [OutlookSyncStatus.Warning]: 'Warning',
  [OutlookSyncStatus.Syncing]: 'Syncing',
  [OutlookSyncStatus.Enqueued]: 'Syncing',
  [OutlookSyncStatus.Problem]: 'Problem',
};

export function mapHistory(
  history: SyncHistory[] | null | undefined,
  idPrefix: string,
): OutlookSyncHistoryEntry[] {
  return (history ?? []).map((entry, index) => ({
    id: `${entry.startedAt ?? entry.completedAt ?? idPrefix}-${index}`,
    syncStatus: STATUS_MAP[entry.syncStatus] ?? 'Problem',
    amountOfRecordsInSync: entry.amountOfRecordsInSync,
    amountOfRecordsIgnored: entry.amountOfRecordsIgnored,
    startedAt: entry.startedAt ?? entry.completedAt ?? '',
    completedAt: entry.completedAt ?? entry.startedAt ?? '',
    elapsedTimeInMins: Math.max(
      Math.round((entry.elapsedTimeInSeconds ?? 0) / 60),
      0,
    ),
    message: entry.message ?? undefined,
    syncType: entry.syncType,
  }));
}

export function resolveConfigurationId(
  configuration: UserSyncConfiguration | null,
  payload: OutlookSyncConfigurationPayload | null,
): string | null {
  return configuration?.id ?? payload?.userSyncConfigurationId ?? null;
}

// Shared history-refresh pipeline for every sync tab (calendar/email/contact/task).
export function loadSyncHistory(deps: {
  service: UserSyncConfigurationService;
  destroyRef: DestroyRef;
  syncType: OutlookSyncType;
  idPrefix: string;
  errorMessage: string;
  configurationId: string | null;
  isCurrent: () => boolean;
  history: WritableSignal<OutlookSyncHistoryEntry[]>;
  loading: WritableSignal<boolean>;
}): void {
  const {
    service,
    destroyRef,
    syncType,
    idPrefix,
    errorMessage,
    configurationId,
    isCurrent,
    history,
    loading,
  } = deps;

  if (!configurationId) {
    history.set([]);
    loading.set(false);
    return;
  }

  loading.set(true);
  service
    .getHistoryBySyncType(syncType, configurationId, HISTORY_PAGE_SIZE)
    .pipe(
      takeUntilDestroyed(destroyRef),
      finalize(() => loading.set(false)),
    )
    .subscribe({
      next: (response) => {
        if (isCurrent()) {
          history.set(mapHistory(response, idPrefix));
        }
      },
      error: (error) => {
        console.error(errorMessage, error);
        if (isCurrent()) {
          history.set([]);
        }
      },
    });
}
